import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({ name: '', phone: '', company: '', employees: '', request: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    return /^\+?\d{10,15}$/.test(cleanPhone);
  };

  const normalizePhone = (phone: string): string => {
    let clean = phone.replace(/[^\d+]/g, '');
    if (!clean.startsWith('+')) clean = '+' + clean;
    return clean;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setErrorMessage('Пожалуйста, укажите ваше имя'); return; }
    if (!formData.phone.trim()) { setErrorMessage('Пожалуйста, укажите ваш телефон'); return; }
    if (!privacyAccepted) { setErrorMessage('Пожалуйста, подтвердите согласие на обработку персональных данных'); return; }
    if (!validatePhone(formData.phone)) { setErrorMessage('Пожалуйста, введите корректный номер телефона (минимум 10 цифр)'); return; }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const honeypotValue = (document.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value || '';
      if (honeypotValue.trim()) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', company: '', employees: '', request: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
        return;
      }

      const normalizedPhone = normalizePhone(formData.phone);
      const baseUrl = 'https://businesstrainer.bitrix24.by/rest/1/yxbm0u72myjrxywc/crm.lead.add.json';
      const comments = [
        formData.request ? `Запрос: ${formData.request}` : '',
        formData.employees ? `Сотрудников: ${formData.employees}` : '',
      ].filter(Boolean).join('. ');
      const params = new URLSearchParams({
        'fields[NAME]': formData.name,
        'fields[TITLE]': `Лид с businesstraining razvitie-personala ${formData.name}`,
        'fields[COMPANY_TITLE]': formData.company,
        'fields[PHONE][0][VALUE]': normalizedPhone,
        'fields[COMMENTS]': comments,
        'params[REGISTER_SONET_EVENT]': 'Y',
        'ASSIGNED_BY_ID': '1',
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`, { method: 'GET' });

      if (response.ok) {
        const result = await response.json();
        if (result.result) {
          setFormStatus('success');
          setFormData({ name: '', phone: '', company: '', employees: '', request: '' });
          setTimeout(() => setFormStatus('idle'), 5000);
        } else {
          throw new Error('Ошибка отправки формы');
        }
      } else {
        throw new Error('Ошибка отправки формы');
      }
    } catch {
      setFormStatus('error');
      setErrorMessage('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
      setTimeout(() => { setFormStatus('idle'); setErrorMessage(''); }, 5000);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-10 md:p-16 text-white text-center mb-10">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 md:mb-6">
            Развивайте сотрудников системно
          </h2>
          <p className="text-sm md:text-base text-white/90 max-w-2xl mx-auto leading-relaxed mb-8">
            Если компании необходимо развивать компетенции сотрудников, повысить самостоятельность команды, создать кадровый резерв или связать обучение с задачами бизнеса — обсудим задачу и подберём подходящий формат.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => document.getElementById('pd-contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-teal-700 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              Заказать программу развития
              <i className="ri-arrow-right-line"></i>
            </button>
            <button
              onClick={() => document.getElementById('pd-contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="border border-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-white/10 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              Обсудить задачу компании
              <i className="ri-chat-3-line"></i>
            </button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-gray-50 rounded-2xl p-5 sm:p-8 border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 text-center mb-6">Оставьте заявку</h3>
            <form id="pd-contact-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="pd_name" className="block text-sm font-semibold text-gray-900 mb-1.5">Имя <span className="text-red-500">*</span></label>
                  <input type="text" id="pd_name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm bg-white" placeholder="Введите ваше имя" />
                </div>
                <div>
                  <label htmlFor="pd_phone" className="block text-sm font-semibold text-gray-900 mb-1.5">Телефон <span className="text-red-500">*</span></label>
                  <input type="tel" id="pd_phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm bg-white" placeholder="+375 (XX) XXX-XX-XX" />
                </div>
                <div>
                  <label htmlFor="pd_company" className="block text-sm font-semibold text-gray-900 mb-1.5">Компания</label>
                  <input type="text" id="pd_company" name="company" value={formData.company} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm bg-white" placeholder="Название вашей компании" />
                </div>
                <div>
                  <label htmlFor="pd_employees" className="block text-sm font-semibold text-gray-900 mb-1.5">Количество сотрудников</label>
                  <select id="pd_employees" name="employees" value={formData.employees} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm bg-white appearance-none cursor-pointer" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27%23475569%27%3E%3Cpath d=%27M12 16L6 10h12z%27/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}>
                    <option value="">Выберите...</option>
                    <option value="1-5">1–5 человек</option>
                    <option value="5-15">5–15 человек</option>
                    <option value="15-50">15–50 человек</option>
                    <option value="50-100">50–100 человек</option>
                    <option value="100+">Более 100 человек</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="pd_request" className="block text-sm font-semibold text-gray-900 mb-1.5">Запрос</label>
                  <textarea id="pd_request" name="request" value={formData.request} onChange={handleInputChange} maxLength={500} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm bg-white resize-none" placeholder="Опишите ваш запрос (до 500 символов)" />
                  <p className="text-xs text-gray-500 mt-1">{formData.request.length}/500 символов</p>
                </div>

                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="opacity-0 absolute -z-10 pointer-events-none" />

                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{errorMessage}</div>
                )}
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center"><i className="ri-check-line mr-1.5 text-lg"></i> Спасибо! Заявка отправлена. Свяжусь с вами в течение одного рабочего дня.</div>
                )}

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600 cursor-pointer flex-shrink-0" />
                  <span className="text-xs text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">Я согласен(на) на <a href="https://sivko.by/load/privacy-policy.pdf" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 underline">обработку персональных данных</a></span>
                </label>

                <button type="submit" disabled={formStatus === 'submitting' || !privacyAccepted} className="w-full bg-teal-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer">
                  {formStatus === 'submitting' ? 'Отправка...' : 'Отправить заявку'}
                </button>
              </div>
            </form>
            <p className="text-xs text-gray-500 text-center mt-4">
              Отвечаю лично в течение одного рабочего дня.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}